import React from 'react';
import { useIntl } from 'react-intl';
import { gql, useQuery } from '@apollo/client';
import { TrendingUp as TrendingUpIcon, ArrowRight as ArrowRightIcon, Calendar as CalendarIcon } from 'lucide-react';
import InfoTooltip from '../common/InfoTooltip';
import { currencySymbol } from '../../utils/formatters';

// Minimal fields to detect valuation + values; adjust to match backend types if needed
const GET_VALUATION_SNAPSHOT = gql`
  query GetValuationSnapshot($input: Input) {
    successionValuationData(input: $input) {
      valueOfPractice
      sumDiscountedCashFlows
      discountedTerminalValue
    }
  }
`;

const GET_CURRENT_USER_VAL = gql`
  query GetCurrentUserVal {
    getCurrentUser {
      id
      valuation_last_accessed_date
      readiness_goal_type
      readiness_goal_lump_sum
      readiness_goal_annual_income
      readiness_withdrawal_rate
    }
  }
`;

const formatCurrency = (value) => {
  const v = Number(value || 0);
  const sym = currencySymbol();
  if (v >= 1000000) {
    return `${sym}${(v / 1000000).toFixed(1)}M`;
  }
  if (v >= 1000) {
    return `${sym}${(v / 1000).toFixed(0)}K`;
  }
  return `${sym}${v.toLocaleString()}`;
};

const formatRelativeTime = (dateStr) => {
  if (!dateStr) return null;
  try {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffWeeks = Math.floor(diffDays / 7);

    if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    if (diffWeeks < 4) return `${diffWeeks} week${diffWeeks !== 1 ? 's' : ''} ago`;
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch (_) {
    return null;
  }
};

const APPROACH_TOOLTIPS = {
  DCF: 'Discounted Cash Flow estimates value by projecting future cash flows and discounting them to present value.',
  MOR: 'Multiple of Revenue estimates value by applying an industry multiple to recurring and non-recurring revenue.',
  EBITDA: 'EBITDA-based valuation applies a market multiple to earnings before interest, taxes, depreciation, and amortization.',
};

// Empty State Component
const EmptyState = ({ canCreate = true, showHeaderIcon = true, intl }) => (
  <div className="space-y-4 h-full flex flex-col">
    {/* Header */}
    <div className={showHeaderIcon ? "flex items-start gap-4" : ""}>
      {showHeaderIcon && (
        <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 bg-succession-success/10">
          <TrendingUpIcon className="w-6 h-6 text-succession-success" />
        </div>
      )}
      <div>
        <h3 className="font-semibold text-lg text-succession-text-primary leading-tight" style={{ margin: 0, padding: 0 }}>{intl.formatMessage({ id: 'succession.launchpad.valuation_snapshot_title' })}</h3>
        <p className="text-sm text-succession-text-secondary leading-tight" style={{ margin: 0, padding: 0 }}>
          {intl.formatMessage({ id: 'succession.launchpad.valuation_snapshot_subtitle_empty' })}
        </p>
      </div>
    </div>

    {/* Description */}
    <p className="text-sm text-succession-text-secondary leading-relaxed flex-1">
      {intl.formatMessage({ id: 'succession.launchpad.valuation_snapshot_description' })}
    </p>

    {/* Action */}
    {canCreate ? (
      <a
        href="/succession/valuation"
        className="flex items-center justify-center w-full px-4 py-2 text-sm font-medium rounded-md bg-succession-text-primary text-succession-bg-primary hover:opacity-90 transition-opacity group"
      >
        {intl.formatMessage({ id: 'succession.launchpad.complete_valuation' })}
        <ArrowRightIcon className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
      </a>
    ) : (
      <div className="text-center py-3 px-4 bg-succession-bg-secondary rounded-md">
        <p className="text-sm text-succession-text-secondary" style={{ margin: 0 }}>
          {intl.formatMessage({ id: 'succession.launchpad.contact_advisor_valuation' })}
        </p>
      </div>
    )}
  </div>
);

// Active State Component with Donut Chart
const ActiveState = ({ data, lastUpdated, showHeaderIcon = true, compact = false, intl, showApproachTooltips = false }) => {
  const sum = Number(data.sumDiscountedCashFlows || 0);
  const term = Number(data.discountedTerminalValue || 0);
  const total = sum + term;
  const practiceValue = Number(data.valueOfPractice || total);

  // Calculate percentages for the donut
  const cashFlowsPercent = total > 0 ? (sum / total) * 100 : 50;
  const terminalValuePercent = total > 0 ? (term / total) * 100 : 50;

  // SVG donut chart parameters - smaller for compact mode (sidebar)
  const size = compact ? 144 : 205;
  const strokeWidth = compact ? 24 : 32;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;

  // Calculate stroke dash arrays for segments
  const cashFlowsDash = (cashFlowsPercent / 100) * circumference;
  const terminalValueDash = (terminalValuePercent / 100) * circumference;

  // Theme-aware colors
  const COLOR_CASH_FLOWS = 'var(--succession-primary, #5489B6)';
  const COLOR_TERMINAL = 'var(--succession-success, #10b981)';

  const formattedLastUpdated = formatRelativeTime(lastUpdated);

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className={`${compact ? 'mb-4' : 'mb-6'} ${showHeaderIcon ? "flex items-start gap-4" : ""}`}>
        {showHeaderIcon && (
          <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 bg-succession-success/10">
            <TrendingUpIcon className="w-6 h-6 text-succession-success" />
          </div>
        )}
        <div>
          <h3 className="font-semibold text-lg text-succession-text-primary leading-tight" style={{ margin: 0, padding: 0 }}>{intl.formatMessage({ id: 'succession.launchpad.valuation_snapshot_title' })}</h3>
          <p className="text-sm text-succession-text-secondary leading-tight" style={{ margin: 0, padding: 0 }}>
            {intl.formatMessage({ id: 'succession.launchpad.valuation_snapshot_subtitle_buyer' })}
          </p>
        </div>
      </div>

      {/* Donut Chart */}
      <div className={`flex justify-center flex-1 ${compact ? 'mb-4' : 'mb-8'}`}>
        <div className="relative" style={{ width: size, height: size }}>
          <svg width={size} height={size} className="transform -rotate-90">
            {/* Background circle */}
            <circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              fill="none"
              stroke="var(--succession-bg-secondary, #f3f4f6)"
              strokeWidth={strokeWidth}
            />

            {/* Sum of Cash Flows segment (primary/blue) */}
            <circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              fill="none"
              stroke={COLOR_CASH_FLOWS}
              strokeWidth={strokeWidth}
              strokeDasharray={`${cashFlowsDash} ${circumference}`}
              strokeLinecap="round"
            />

            {/* Discounted Terminal Value segment (success/green) */}
            <circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              fill="none"
              stroke={COLOR_TERMINAL}
              strokeWidth={strokeWidth}
              strokeDasharray={`${terminalValueDash} ${circumference}`}
              strokeDashoffset={-cashFlowsDash}
              strokeLinecap="round"
            />
          </svg>

          {/* Center text */}
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <p className={`${compact ? 'text-2xl' : 'text-4xl'} font-bold text-succession-text-primary`} style={{ margin: 0 }}>{formatCurrency(practiceValue)}</p>
            <p className={`${compact ? 'text-xs' : 'text-sm'} text-succession-text-secondary mt-1`} style={{ margin: 0 }}>{intl.formatMessage({ id: 'succession.launchpad.practice_value' })}</p>
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className={`grid grid-cols-2 ${compact ? 'gap-2 py-4' : 'gap-4 py-6'} border-t border-succession-border`}>
        <div className={`flex items-start ${compact ? 'gap-2' : 'gap-3'}`}>
          <div
            className={`${compact ? 'w-3 h-3' : 'w-4 h-4'} rounded-sm mt-1 flex-shrink-0`}
            style={{ backgroundColor: COLOR_CASH_FLOWS }}
          />
          <div>
            <p className={`${compact ? 'text-xs' : 'font-medium'} text-succession-text-primary`} style={{ margin: 0 }}>{intl.formatMessage({ id: 'succession.launchpad.sum_of_cash_flows' })}</p>
            <p className={`${compact ? 'text-sm' : 'text-lg'} font-semibold text-succession-text-primary`} style={{ margin: 0 }}>
              {formatCurrency(sum)}
            </p>
          </div>
        </div>
        <div className={`flex items-start ${compact ? 'gap-2' : 'gap-3'}`}>
          <div
            className={`${compact ? 'w-3 h-3' : 'w-4 h-4'} rounded-sm mt-1 flex-shrink-0`}
            style={{ backgroundColor: COLOR_TERMINAL }}
          />
          <div>
            <p className={`${compact ? 'text-xs' : 'font-medium'} text-succession-text-primary`} style={{ margin: 0 }}>{intl.formatMessage({ id: 'succession.launchpad.discounted_terminal_value' })}</p>
            <p className={`${compact ? 'text-sm' : 'text-lg'} font-semibold text-succession-text-primary`} style={{ margin: 0 }}>
              {formatCurrency(term)}
            </p>
          </div>
        </div>
      </div>

      {/* Last Updated */}
      {formattedLastUpdated && (
        <div className="flex items-center gap-2 text-sm text-succession-text-secondary">
          <CalendarIcon className="w-4 h-4" />
          <span>{intl.formatMessage({ id: 'succession.launchpad.last_updated' }, { time: formattedLastUpdated })}</span>
        </div>
      )}

      {showApproachTooltips && (
        <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-succession-text-secondary">
          <span className="font-medium text-succession-text-primary">Approaches used:</span>
          {Object.keys(APPROACH_TOOLTIPS).map((approach) => (
            <span key={approach} className="inline-flex items-center gap-1">
              <span className="font-semibold text-succession-text-primary">{approach}</span>
              <InfoTooltip content={APPROACH_TOOLTIPS[approach]} />
            </span>
          ))}
        </div>
      )}

      {/* Footer */}
      <div className="flex justify-end">
        <a
          href="/succession/valuation"
          className="inline-flex items-center text-sm font-medium text-succession-primary hover:text-succession-primary-hover transition-colors group p-0"
        >
          {intl.formatMessage({ id: 'succession.launchpad.see_valuation' })}
          <ArrowRightIcon className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
        </a>
      </div>
    </div>
  );
};

const ValuationSnapshot = ({
  showHeaderIcon = true, // Set to false for sidebar usage
  compact = false, // Set to true for narrower columns (sidebar)
  showApproachTooltips = false,
  className = '' 
}) => {
  const intl = useIntl();
  // First, lightweight user check, do NOT mutate last_accessed
  const { data: cu, loading: cuLoading, error: cuError } = useQuery(GET_CURRENT_USER_VAL, { 
    fetchPolicy: 'cache-first', 
    nextFetchPolicy: 'cache-first' 
  });
  
  const lastAccessed = cu?.getCurrentUser?.valuation_last_accessed_date;
  const canCreate = true; // All users can access the valuation page
  const shouldSkipSnapshot = cuLoading || !lastAccessed;

  const { data, loading, error } = useQuery(GET_VALUATION_SNAPSHOT, { 
    variables: { input: null }, 
    skip: shouldSkipSnapshot, 
    fetchPolicy: 'network-only', 
    nextFetchPolicy: 'cache-first' 
  });

  const overallLoading = cuLoading || (!shouldSkipSnapshot && loading);
  const overallError = cuError || error;

  // Loading state
  if (overallLoading) {
    return (
      <div className={`bg-succession-bg-primary overflow-hidden shadow-succession-md rounded-lg border border-succession-border p-6 h-full ${className}`}>
        <div className="flex items-start gap-4">
          {showHeaderIcon && <div className="w-12 h-12 rounded-full bg-succession-bg-secondary animate-pulse" />}
          <div className="flex-1 space-y-2">
            <div className="h-5 bg-succession-bg-secondary rounded w-32 animate-pulse" />
            <div className="h-4 bg-succession-bg-secondary rounded w-48 animate-pulse" />
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (overallError) {
    return (
      <div className={`bg-succession-bg-primary overflow-hidden shadow-succession-md rounded-lg border border-succession-border p-6 h-full ${className}`}>
        <div className="text-sm text-succession-error">{intl.formatMessage({ id: 'succession.launchpad.unable_to_load_valuation' })}</div>
      </div>
    );
  }

  const snapshot = (!shouldSkipSnapshot && data) ? (data?.successionValuationData || null) : null;
  const hasData = Boolean(snapshot && (snapshot.valueOfPractice || snapshot.sumDiscountedCashFlows || snapshot.discountedTerminalValue));

  return (
    <div className={`bg-succession-bg-primary overflow-hidden shadow-succession-md rounded-lg border border-succession-border ${compact ? 'p-4' : 'p-6'} hover:shadow-succession-lg transition-shadow h-full ${className}`}>
      {hasData ? (
        <ActiveState
          data={snapshot}
          lastUpdated={lastAccessed}
          showHeaderIcon={showHeaderIcon}
          compact={compact}
          showApproachTooltips={showApproachTooltips}
          intl={intl}
        />
      ) : (
        <EmptyState
          canCreate={canCreate}
          showHeaderIcon={showHeaderIcon}
          intl={intl}
        />
      )}
    </div>
  );
};

export default ValuationSnapshot;
